import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps {
  tone: AlertTone;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

const toneConfig: Record<AlertTone, { classes: string; Icon: typeof Info }> = {
  info: { classes: 'bg-info/10 border-info/40 text-info', Icon: Info },
  success: { classes: 'bg-success/10 border-success/40 text-success', Icon: CheckCircle2 },
  warning: { classes: 'bg-warning/10 border-warning/40 text-warning', Icon: AlertTriangle },
  danger: { classes: 'bg-danger/10 border-danger/40 text-danger', Icon: XCircle },
};

/**
 * Full-width, subtle-bg + border from the semantic color. Used for the
 * bot-degraded banner, verification-not-configured notices, etc.
 * docs/DESIGN_SYSTEM.md §4.18.
 */
export function Alert({ tone, title, description, action }: AlertProps) {
  const { classes, Icon } = toneConfig[tone];
  return (
    <div className={['flex gap-3 rounded-md border p-4', classes].join(' ')} role="status">
      <Icon size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-body-medium text-text-primary">{title}</p>
        {description ? (
          <p className="text-small text-text-secondary mt-0.5">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
